import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';

import * as d3 from 'd3';
import * as d3Scale from 'd3-scale';
import * as d3Shape from 'd3-shape';

import { ColorLevel, ColorName, ColorsService, NumbersService } from '@common/services';

import { DataColumn, DataColumnPoint } from '../../../models';
import { BaseChartComponent } from '../base';

@Component({
    selector: 'aa-donut',
    templateUrl: './donut.component.html',
    styleUrls: [
        '../../../container.scss',
        './donut.component.scss'
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DonutComponent extends BaseChartComponent<DataColumn>
{
    @Input() isClickable: boolean = false;
    @Output() pieClicked: EventEmitter<any> = new EventEmitter<any>();
    private radius: number;
    private translate2Center: string;
    private innerRadius: number;
    private outerRadius: number;
    private arc: any;
    private pie: any;
    private color: any;
    private colorHover: any;
    private circleText: any;
    private centerText1: any;
    private total: number = 0;

    constructor (
        private numbersService: NumbersService
    )
    {
        super();
    }

    protected drawChart (): void
    {
        this.initSvg();

        const t: string = 'total';
        const localData: any = this.data.points
            .map((p: DataColumnPoint) =>
            {
                const o: any = { x: p.x };
                let sum = 0;

                for (const [, v] of p.values)
                {
                    sum += v;
                }

                for (const [k, v] of p.values)
                {
                    o[k] = v;
                }

                o[t] = sum;

                this.total += sum;
                return o;
            });

        const slices = this.gpeChart
            .append('g')
            .attr('class', 'gpeSlices')
            .attr('transform', this.translate2Center)
            .selectAll('.arc')
            .data(this.pie(localData))
            .enter()
            .append('g')
            .attr('class', () =>
                this.isClickable ? 'arc clickable' : 'arc'
            );

        slices.append('path')
            .attr('d', this.arc)
            .style('fill', (d: any) =>
                this.color(d.data.x)
            )
            .on(
                'mouseover',
                (event: any, d: any) =>
                    this.sliceMouseover(event.target, d)
            )
            .on(
                'mouseout',
                (event: any, d: any) =>
                {
                    this.sliceMouseout(event.target, d);
                    this.drawTotalCircle();
                }
            )
            .on(
                'click',
                (event: any, d: any) =>
                {
                    if (this.isClickable)
                    {
                        this.pieClicked.emit(d.data);
                    }
                }
            );

        this.circleText = this.gpeChart.append('circle')
            .attr('transform', this.translate2Center)
            .attr('cx', 0)
            .attr('cy', 0)
            .attr('r', 2 * this.innerRadius / 3)
            .style('fill', 'transparent');

        this.centerText1 = this.gpeChart.append('text')
            .attr('transform', this.translate2Center)
            .attr('class', 'center-txt value')
            .attr('text-anchor', 'middle')
            .attr('dominant-baseline', 'middle')
            .style('font-weight', 'bold');

        this.drawTotalCircle();

        const legends = slices.append('text')
            .attr('transform', (d: any) =>
                `translate(${this.arc.centroid(d)})`
            )
            .attr('class', 'title')
            .style('fill', (d: any) =>
                this.colorHover(d.data.x)
            )
            .attr('dy', '.35em')
            .text((d: any) =>
                d.data.x
            )
            .style('text-anchor', 'middle');

        legends
            .on(
                'click',
                (event: any, d: any) =>
                {
                    if (this.isClickable)
                    {
                        this.pieClicked.emit(d.data);
                    }
                }
            )
            .on(
                'mouseover',
                (event: any, d: any) =>
                    this.sliceMouseover(event.target, d)
            )
            .on(
                'mouseout',
                (event: any, d: any) =>
                    this.sliceMouseout(event.target, d)
            );
    }

    protected initSvg (): void
    {
        this.baseInitSvg();

        this.radius = Math.min(this.dimensions.boundedWidth, this.dimensions.boundedHeight) / 2;
        this.outerRadius = this.radius - 10;
        this.innerRadius = (this.radius - 10) / 2;
        this.translate2Center = `translate(${this.dimensions.boundedWidth / 2},${this.dimensions.boundedHeight / 2})`;

        this.color = d3Scale.scaleOrdinal()
            .range(this.data.colors);

        this.colorHover = d3Scale.scaleOrdinal()
            .range(this.data.colorsHover);

        this.arc = d3Shape.arc()
            .innerRadius(this.innerRadius)
            .outerRadius(this.outerRadius);

        this.pie = d3Shape.pie()
            .value((d: any) => d[this.data.keys[0]]);
    }

    private drawTotalCircle ()
    {
        this.circleText
            .style('fill', ColorsService.getBasicColor(ColorLevel.Normal, ColorName.Grey2));
        this.centerText1
            .text(() =>
                this.numbersService.toFixed(this.total, 0)
            )
            .style('fill', ColorsService.getBasicColor(ColorLevel.Normal, ColorName.Black));
    }

    private sliceMouseover (htmlElt: any, d: any)
    {
        const pathNode = d3.select(htmlElt).select('path');

        pathNode
            .style('fill', this.colorHover(d.data.x));
        d3.select(htmlElt)
            .select('.title')
            .style('fill', this.color(d.data.x));
        this.circleText
            .style('fill', this.color(d.data.x));
        this.centerText1
            .text(() =>
                this.numbersService.toFixed(d.data.total, 0)
            )
            .style('fill', this.colorHover(d.data.x));
    }

    private sliceMouseout (node: any, d: any)
    {
        const pathNode = d3.select(node).select('path');

        pathNode
            .style('fill', this.color(d.data.x));
        d3.select(node)
            .select('.title')
            .style('fill', this.colorHover(d.data.x));
    }
}
